/**
 * Progression — chord sequences and Roman numeral analysis.
 *
 * Provides:
 * - Diatonic chord generation for a key (the seven triads and seventh chords)
 * - Common progression templates (I-IV-V, ii-V-I, etc.)
 * - Cadence classification (perfect authentic, plagal, deceptive, half)
 */

import { type Key, diatonicRomanNumeral } from "./key.js";
import { type Chord, chord, type ChordQuality } from "./chord.js";
import { scaleDegree } from "./scale.js";
import { parseInterval, transposeByInterval } from "./interval.js";

/**
 * The seven diatonic triads of a key, indexed by scale degree (1-7).
 *
 * Major: I (maj), ii (min), iii (min), IV (maj), V (maj), vi (min), vii° (dim)
 * Minor: i (min), ii° (dim), III (maj), iv (min), v (min), VI (maj), VII (maj)
 *   (natural minor — for harmonic minor, V becomes major and vii° appears)
 */
export function diatonicTriads(k: Key): Chord[] {
  const qualities = k.mode === "major"
    ? ["maj", "min", "min", "maj", "maj", "min", "dim"] as const
    : ["min", "dim", "maj", "min", "min", "maj", "maj"] as const;
  return qualities.map((q, i) => chord(scaleDegree(k.scale, i + 1), q as ChordQuality));
}

/** The seven diatonic seventh chords of a key. */
export function diatonicSevenths(k: Key): Chord[] {
  const qualities = k.mode === "major"
    ? ["maj7", "min7", "min7", "maj7", "dom7", "min7", "min7b5"] as const
    : ["min7", "min7b5", "maj7", "min7", "min7", "maj7", "dom7"] as const;
  return qualities.map((q, i) => chord(scaleDegree(k.scale, i + 1), q as ChordQuality));
}

/**
 * A progression is a sequence of (degree, quality?) pairs in a key.
 * Use `null` for quality to default to the diatonic quality.
 */
export interface ProgressionStep {
  degree: number; // 1-7
  /** Override the diatonic quality (for things like V7, applied dominants). */
  qualityOverride?: ChordQuality;
}

export interface ProgressionTemplate {
  name: string;
  steps: ProgressionStep[];
  /** Common label like "I-IV-V-I" or "ii-V-I". */
  label: string;
}

/** Common progression templates by name. */
export const PROGRESSION_TEMPLATES: Record<string, ProgressionTemplate> = {
  "I-IV-V-I": {
    name: "Authentic cadence (I-IV-V-I)",
    label: "I-IV-V-I",
    steps: [{ degree: 1 }, { degree: 4 }, { degree: 5 }, { degree: 1 }],
  },
  "I-V-vi-IV": {
    name: "Pop progression (I-V-vi-IV)",
    label: "I-V-vi-IV",
    steps: [{ degree: 1 }, { degree: 5 }, { degree: 6 }, { degree: 4 }],
  },
  "ii-V-I": {
    name: "Jazz turnaround (ii-V-I)",
    label: "ii-V-I",
    steps: [
      { degree: 2, qualityOverride: "min7" },
      { degree: 5, qualityOverride: "dom7" },
      { degree: 1, qualityOverride: "maj7" },
    ],
  },
  "I-vi-IV-V": {
    name: "Doo-wop (I-vi-IV-V)",
    label: "I-vi-IV-V",
    steps: [{ degree: 1 }, { degree: 6 }, { degree: 4 }, { degree: 5 }],
  },
  "vi-IV-I-V": {
    name: "Pop minor-feel (vi-IV-I-V)",
    label: "vi-IV-I-V",
    steps: [{ degree: 6 }, { degree: 4 }, { degree: 1 }, { degree: 5 }],
  },
  "I-V-vi-iii-IV-I-IV-V": {
    name: "Pachelbel's Canon",
    label: "I-V-vi-iii-IV-I-IV-V",
    steps: [
      { degree: 1 }, { degree: 5 }, { degree: 6 }, { degree: 3 },
      { degree: 4 }, { degree: 1 }, { degree: 4 }, { degree: 5 },
    ],
  },
  "I-IV-I-V": {
    name: "Plagal-authentic mix (I-IV-I-V)",
    label: "I-IV-I-V",
    steps: [{ degree: 1 }, { degree: 4 }, { degree: 1 }, { degree: 5 }],
  },
};

/** Materialize a progression template into actual chords for a given key. */
export function realizeProgression(template: ProgressionTemplate, k: Key): Chord[] {
  return template.steps.map((step) => {
    if (step.qualityOverride) {
      return chord(scaleDegree(k.scale, step.degree), step.qualityOverride);
    }
    // Use the diatonic quality.
    const diatonic = diatonicTriads(k);
    return diatonic[step.degree - 1]!;
  });
}

/**
 * Cadence classification — examines the last two chords of a progression.
 *
 * - Perfect Authentic Cadence (PAC): V → I, both root position, soprano on tonic
 * - Imperfect Authentic Cadence (IAC): V → I, less strict
 * - Plagal: IV → I
 * - Half: any → V
 * - Deceptive: V → vi (or V → anything but I)
 */
export type CadenceType = "PAC" | "IAC" | "plagal" | "half" | "deceptive" | "none";

export function classifyCadence(chords: Chord[], k: Key): CadenceType {
  if (chords.length < 2) return "none";
  const penultimate = chords[chords.length - 2]!;
  const final = chords[chords.length - 1]!;
  // Determine each chord's scale degree (1-7) by matching root to scale.
  const triads = diatonicTriads(k);
  const findDegree = (c: Chord): number | null => {
    for (let i = 0; i < triads.length; i++) {
      if (triads[i]!.root.letter === c.root.letter && triads[i]!.root.accidental === c.root.accidental) {
        return i + 1;
      }
    }
    return null;
  };
  const penDeg = findDegree(penultimate);
  const finDeg = findDegree(final);
  if (penDeg === 5 && finDeg === 1) {
    // Distinguish PAC vs IAC by inversion. Strict PAC requires both root position.
    if (penultimate.inversion === 0 && final.inversion === 0) return "PAC";
    return "IAC";
  }
  if (penDeg === 4 && finDeg === 1) return "plagal";
  if (finDeg === 5) return "half";
  if (penDeg === 5 && finDeg !== 1) return "deceptive";
  return "none";
}

/* ─── Secondary dominants — tonicization ─────────────────────────────────── */

/**
 * Result of {@link tonicization}: the secondary dominant chord and the chord
 * it tonicizes (the "local tonic"), plus a Roman-numeral label like "V/ii".
 * The tonicized chord is exposed so callers don't have to reach back into
 * `diatonicTriads`/`scaleDegree` themselves.
 */
export interface Tonicization {
  /** The secondary dominant chord (V/x). Defaults to dom7 quality — the
   *  characteristic sound of a tonicizing dominant. */
  dominant: Chord;
  /** The chord being tonicized (= the diatonic chord on `targetDegree`). */
  target: Chord;
  /** Roman-numeral label, e.g. "V/ii" or "V7/ii". */
  label: string;
  /** The host-key degree being tonicized (2..6 in major; non-diminished). */
  targetDegree: number;
}

/**
 * Build a secondary dominant for a host key + target diatonic degree.
 *
 * The dominant is built a perfect 5th above the target chord's root and uses
 * `dom7` quality by default. `target` returns the host-key diatonic chord on
 * `targetDegree` (so the tonicizing chord and the chord it resolves to come
 * out of one helper).
 *
 * Diminished targets are out of scope — V/vii° in major and V/ii° in minor
 * tonicize a diminished chord, which is musically degenerate (the "local
 * tonic" wouldn't actually feel tonic). Curriculum drops them; this helper
 * throws if you ask for one.
 *
 * `targetDegree` of 1 (V/I = V) is also rejected: tonicizing the actual
 * tonic is just the home-key dominant, not a secondary.
 */
export function tonicization(
  k: Key,
  targetDegree: number,
  opts: { seventh?: boolean } = {},
): Tonicization {
  if (targetDegree < 2 || targetDegree > 7) {
    throw new Error(`Tonicization target must be a non-tonic degree 2..7, got ${targetDegree}`);
  }
  const triads = diatonicTriads(k);
  const target = triads[targetDegree - 1]!;
  // A diminished triad has a d5 (6 semitones) where a perfect 5th would sit.
  const targetIsDiminished = target.intervals[2]?.semitones === 6;
  if (targetIsDiminished) {
    throw new Error(
      `Cannot tonicize diminished degree ${targetDegree} (${diatonicRomanNumeral(targetDegree, k.mode)}) in ${k.name}`,
    );
  }
  const seventh = opts.seventh !== false;
  const dominantQuality: ChordQuality = seventh ? "dom7" : "maj";
  const p5 = parseInterval("P5")!;
  const dominantRoot = transposeByInterval(target.root, p5);
  const dominant = chord(dominantRoot, dominantQuality);
  const targetRoman = diatonicRomanNumeral(targetDegree, k.mode);
  const label = `${seventh ? "V7" : "V"}/${targetRoman}`;
  return { dominant, target, label, targetDegree };
}
